import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LoginStatus } from '../account/shared/login-status-service';
import { AutoLogoutService } from '../account/shared/auto-logout.service';
import { UserService } from '../account/shared/user.service';
import { environment } from '../../environments/environment';

/**
 * Catches 401 responses from any API and attempts a one-shot refresh using the
 * stored fa-zz-auth refresh token. On successful refresh the original request
 * is replayed with the new access token; concurrent in-flight requests wait
 * for the same refresh and then replay. On refresh failure (or if we 401 on
 * the auth endpoints themselves) the session is cleared and the user is sent
 * back to /login.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private refreshInProgress = false;
  private refreshedToken$ = new BehaviorSubject<string | null>(null);

  private readonly router = inject(Router);
  private readonly loginStatus = inject(LoginStatus);
  private readonly autoLogout = inject(AutoLogoutService);
  // UserService is resolved via the Injector to break a circular DI chain
  // (UserService -> HubService -> HttpClient -> this interceptor).
  private readonly injector = inject(Injector);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401) {
          return throwError(() => err);
        }

        // A 401 from the auth endpoints themselves means the credentials or
        // refresh token were rejected — no point retrying.
        if (this.isAuthRequest(req)) {
          return throwError(() => err);
        }

        return this.handleUnauthorised(req, next, err);
      })
    );
  }

  private handleUnauthorised(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    const userService = this.injector.get(UserService);

    if (!userService.getRefreshToken()) {
      this.finalLogout();
      return throwError(() => originalError);
    }

    if (this.refreshInProgress) {
      // Wait for the in-flight refresh to complete, then replay with the new token.
      return this.refreshedToken$.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.withToken(req, token!)))
      );
    }

    this.refreshInProgress = true;
    this.refreshedToken$.next(null);

    return userService.refresh().pipe(
      switchMap(newToken => {
        this.refreshInProgress = false;

        if (!newToken) {
          this.finalLogout();
          return throwError(() => originalError);
        }

        this.refreshedToken$.next(newToken);
        return next.handle(this.withToken(req, newToken));
      }),
      catchError(err => {
        this.refreshInProgress = false;
        this.finalLogout();
        return throwError(() => err);
      })
    );
  }

  private withToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  private isAuthRequest(req: HttpRequest<any>): boolean {
    return req.url.startsWith(environment.authURL);
  }

  private finalLogout(): void {
    this.autoLogout.destroy();
    this.loginStatus.logout();
    this.router.navigate(['/login']);
  }
}
